<script setup lang="ts">
import { computed, ref } from 'vue';
import MeService from '@/core/shared/me/me.service';
import { toSwitchableEmployers } from './switchableEmployers';
import ChevronLeftSvg from '@/assets/svg/chevron-left.svg';

const emit = defineEmits<{ back: []; close: [] }>();

const meService = new MeService();

const search = ref('');

const selectedEmployerId = computed(() => meService.getSelectedEmployerId());

const employers = computed(() => toSwitchableEmployers(meService.userProfile?.employerUserMaps));

const filteredEmployers = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) {
    return employers.value;
  }
  return employers.value.filter((employer) => employer.name.toLowerCase().includes(query));
});

function selectEmployer(employerId: number): void {
  const wasChanged = meService.setSelectedEmployerId(employerId);
  if (wasChanged) {
    // Reset to root so stale project/conversation ids from the previous employer
    // do not 404 under the new context (mirrors the desktop org switcher).
    window.location.replace('/');
    return;
  }
  emit('close');
}
</script>

<template>
  <div class="flex max-h-[inherit] flex-col" data-test="mobile-org-switcher">
    <button
      type="button"
      class="flex items-center gap-1 px-4 py-3 text-left"
      data-test="account-menu-back"
      @click="emit('back')"
    >
      <img :src="ChevronLeftSvg" alt="" width="20" height="20" />
      <span class="text-sm font-bold text-shade-880">Back</span>
    </button>

    <div class="px-3 pb-3">
      <v-text-field
        v-model="search"
        placeholder="Search organizations"
        variant="outlined"
        density="compact"
        hide-details
        autofocus
        data-test="org-search"
      />
    </div>

    <div class="flex-1 overflow-y-auto">
      <button
        v-for="employer in filteredEmployers"
        :key="employer.id"
        type="button"
        :class="[
          'flex w-full items-center px-4 py-3 text-left text-sm',
          employer.id === selectedEmployerId ? 'bg-highlight-50 font-bold' : '',
        ]"
        data-test="org-option"
        @click="selectEmployer(employer.id)"
      >
        {{ employer.name }}
      </button>

      <p
        v-if="filteredEmployers.length === 0"
        class="px-4 py-3 text-sm text-shade-800"
        data-test="org-search-empty"
      >
        No organizations found
      </p>
    </div>
  </div>
</template>
